import React from "react";
import DoorWorkOrderDialog from "../components/WorkOrders/Doors/DoorWorkOrderDialog";
import DoorOrderList from "../components/WorkOrders/Doors/DoorOrderList";
import DoorDataService from "../services/Door.services";
import { Box } from "@mui/material";
import Loading from "../components/Loading";
import DoorSummary from "../components/WorkOrders/Doors/DoorSummary";
import { useLocation } from "react-router-dom";

export default function Doors(props) {
    const { user, token, handleOpenSnackbar, darkState } = props;
    const location = useLocation();

    const [
        openDoorWorkOrderDialog,
        setOpenDoorWorkOrderDialog,
    ] = React.useState(false);
    const [DoorWorkOrder, setDoorWorkOrder] = React.useState("");
    const [doorOrderList, setDoorOrderList] = React.useState([]);
    const [doorNumberList, setDoorNumberList] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true); // wait until API returns promise

    React.useEffect(() => {
        // Check if door data was passed from WorkOrders page
        if (
            location.state &&
            location.state.doorOrderList &&
            location.state.skipInitialFetch
        ) {
            // Use the passed data instead of fetching
            console.log("Using passed door data from WorkOrders page");
            const passedData = location.state.doorOrderList;
            setDoorOrderList(passedData);
            projectList(passedData);
        } else {
            // No passed data, fetch as usual
            console.log("Fetching door data via API");
            retrieveOrders();
        }
    }, []);

    const updateOrder = (id, newDate) => {
        const order = doorOrderList.filter((o) => o.id === id)[0];
        const sanitizeData = {
            ...order,
            project: order.project ? order.project.id : "",
            due: newDate,
            rev:
                order.rev && order.rev.length > 0
                    ? order.rev.map((i) => i.id)
                    : [],
            created_by: order.created_by ? order.created_by.id : "",
            checked_by: order.checked_by ? order.checked_by.id : "",
            door_type: order.door_type ? order.door_type.id : "",
            lockset: order.lockset ? order.lockset.id : "",
            sill_type: order.sill_type ? order.sill_type.id : "",
            frame_type: order.frame_type ? order.frame_type.id : "",
            core_type: order.core_type ? order.core_type.id : "",
            hinge_type: order.hinge_type ? order.hinge_type.id : "",
            options:
                order.options && order.options.length > 0
                    ? order.options.map((i) => i.id)
                    : [],
            packaging: order.packaging ? order.packaging.id : "",
            drawings:
                order.drawings && order.drawings.length > 0
                    ? order.drawings.map((i) => i.id)
                    : [],
            log:
                order.log && order.log.length > 0
                    ? order.log.map((i) => i.id)
                    : [],
            qr_code: order.qr_code ? order.qr_code.id : null,
        };

        DoorDataService.updateDoor(id, sanitizeData, token)
            .then((response) => {
                const updatedOrder = response.data;

                const updatedList = [...doorOrderList];
                const index = updatedList.findIndex(
                    (item) => item.id === updatedOrder.id
                );

                if (index !== -1) {
                    updatedList[index] = {
                        ...updatedList[index],
                        due: updatedOrder.due,
                    };
                    setDoorOrderList(updatedList);
                }
                handleOpenSnackbar("info", "Door was updated");
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            });
    };

    const retrieveOrders = () => {
        setIsLoading(true);
        DoorDataService.getAll(token)
            .then((response) => {
                const data = response.data;
                setDoorOrderList(data);
                projectList(data);
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const projectList = (list) => {
        // create list of project id's
        const projectNumberList = [];
        list.map((p) => {
            if (!projectNumberList.includes(p.project.id)) {
                projectNumberList.push(p.project.id);
            }
        });

        const projectNumberObject = [];
        const promiseArray = projectNumberList.map((p) => {
            return retrieveDoorCount(p)
                .then((countData) => {
                    projectNumberObject.push({ id: p, count: countData.count });
                    // Handle the count data here
                })
                .catch((error) => {
                    console.log(error);
                    // Handle the error here
                });
        });

        Promise.all(promiseArray)
            .then(() => {
                setDoorNumberList(projectNumberObject);
                // Use the updated projectNumberObject here
            })
            .catch((error) => {
                console.log(error);
                // Handle any errors that occurred during Promise.all()
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const retrieveDoorCount = (id) => {
        return new Promise((resolve, reject) => {
            DoorDataService.projectDoorCount(id, token)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    console.log(error);
                    reject(error);
                });
        });
    };

    const updateOrderListWith = (updatedOrder) => {
        if (!updatedOrder || !updatedOrder.id) return;
        console.log("Updating order list with:", updatedOrder);
        setDoorOrderList((prev) => {
            const newList = prev.map((item) =>
                item.id === updatedOrder.id ? updatedOrder : item
            );
            console.log("New door list:", newList);
            return newList;
        });
        setDoorWorkOrder((prev) =>
            prev && prev.id === updatedOrder.id ? updatedOrder : prev
        );
    };

    const markChecked = async (orderId) => {
        if (!token) {
            handleOpenSnackbar(
                "error",
                "No token available to update door work order."
            );
            return;
        }
        try {
            console.log("Marking door as checked:", orderId);
            const response = await DoorDataService.checkedDoor(orderId, token);
            console.log("Checked door response:", response);
            const updatedOrder = response && response.data;
            if (updatedOrder) {
                console.log("Updated order:", updatedOrder);
                updateOrderListWith(updatedOrder);
                setOpenDoorWorkOrderDialog(false);
            }
            handleOpenSnackbar("success", "Door work order marked as checked");
        } catch (error) {
            console.error("Error marking door as checked:", error);
            handleOpenSnackbar(
                "error",
                "Unable to update door work order right now."
            );
        }
    };

    const markUnchecked = async (orderId) => {
        if (!token) {
            handleOpenSnackbar(
                "error",
                "No token available to update door work order."
            );
            return;
        }
        try {
            console.log("Marking door as unchecked:", orderId);
            const response = await DoorDataService.checkedDoor(orderId, token);
            console.log("Unchecked door response:", response);
            const updatedOrder = response && response.data;
            if (updatedOrder) {
                console.log("Updated order:", updatedOrder);
                updateOrderListWith(updatedOrder);
                setOpenDoorWorkOrderDialog(false);
            }
            handleOpenSnackbar(
                "info",
                "Door work order unchecked and marked incomplete"
            );
        } catch (error) {
            console.error("Error marking door as unchecked:", error);
            handleOpenSnackbar(
                "error",
                "Unable to update door work order right now."
            );
        }
    };

    const handleOpenWorkOrder = (order) => {
        setOpenDoorWorkOrderDialog(true);
        setDoorWorkOrder(order);
    };

    return (
        <div
            style={{
                width: "100%",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Box sx={{ mb: 0.5, width: "100%" }}>
                <DoorSummary
                    darkState={darkState}
                    doorOrderList={doorOrderList}
                />
            </Box>
            <DoorOrderList
                handleOpenSnackbar={handleOpenSnackbar}
                darkState={darkState}
                user={user}
                token={token}
                onSelectOrder={handleOpenWorkOrder}
                doorOrderList={doorOrderList}
            />
            <DoorWorkOrderDialog
                openDoorWorkOrderDialog={openDoorWorkOrderDialog}
                setOpenDoorWorkOrderDialog={setOpenDoorWorkOrderDialog}
                order={DoorWorkOrder}
                setDoorWorkOrder={setDoorWorkOrder}
                darkState={darkState}
                user={user}
                onMarkChecked={markChecked}
                onMarkUnchecked={markUnchecked}
            />
            <Loading open={isLoading} />
        </div>
    );
}
